import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "../../lib/apiClient";

const EMPTY = { name: "", origin: "", description: "", sort_order: 0 };

export default function IngredientsManager() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const fetchAll = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ingredients");
            setItems(data);
        } catch (_e) {
            toast.error("Erreur de chargement");
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const reset = () => {
        setEditing(null);
        setForm(EMPTY);
    };

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
        try {
            if (editing) await api.put(`/admin/ingredients/${editing}`, payload);
            else await api.post("/admin/ingredients", payload);
            toast.success("Enregistré");
            reset();
            fetchAll();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || "Erreur");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cet ingrédient ?")) return;
        try {
            await api.delete(`/admin/ingredients/${id}`);
            toast.success("Supprimé");
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    return (
        <div data-testid="admin-ingredients-page">
            <Toaster richColors position="top-center" />
            <p className="section-tag">administration</p>
            <h1 className="font-serif-display text-4xl text-[#2A1408] tracking-tight">
                Ingrédients
            </h1>

            <section className="brand-card p-6 mt-6">
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Nom
                        <input
                            required
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="ing-name"
                        />
                    </label>
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Provenance
                        <input
                            required
                            value={form.origin}
                            onChange={(e) => setForm((f) => ({ ...f, origin: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="ing-origin"
                        />
                    </label>
                    <label className="md:col-span-2 text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Description
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        />
                    </label>
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Ordre
                        <input
                            type="number"
                            value={form.sort_order}
                            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        />
                    </label>
                    <div className="md:col-span-2 flex gap-3">
                        <button type="submit" className="brand-btn" data-testid="ing-submit">
                            {editing ? "Mettre à jour" : "Ajouter"}
                        </button>
                        {editing && (
                            <button type="button" onClick={reset} className="brand-btn-outline">
                                Annuler
                            </button>
                        )}
                    </div>
                </form>
            </section>

            <section className="mt-8 grid md:grid-cols-2 gap-4">
                {items.map((it) => (
                    <article
                        key={it.id}
                        className="brand-card p-5 flex justify-between items-start gap-3"
                        data-testid={`admin-ing-${it.id}`}
                    >
                        <div>
                            <p className="font-serif-display text-lg text-[#2A1408]">
                                {it.name}
                            </p>
                            <p className="text-xs uppercase tracking-widest text-[#B07B66]">
                                {it.origin}
                            </p>
                            {it.description && (
                                <p className="text-sm text-[#5C3D2E] mt-1">{it.description}</p>
                            )}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                    setEditing(it.id);
                                    setForm({
                                        name: it.name,
                                        origin: it.origin,
                                        description: it.description || "",
                                        sort_order: it.sort_order || 0,
                                    });
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className="text-[#6B4226]"
                                aria-label="Modifier"
                            >
                                <Pencil size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => remove(it.id)}
                                className="text-red-700"
                                aria-label="Supprimer"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </article>
                ))}
            </section>
        </div>
    );
}
